import React from "react";
import { Checkbox } from "../../ui/checkbox";
import { LANGUAGE_GROUPS, LANGUAGE_OPTIONS } from "../../../constants/languages";

/**
 * Controlled multi-select for spoken languages (ISO-639-1 codes).
 *
 * Props:
 *   value:    string[]  selected codes
 *   onChange: (next: string[]) => void
 *   disabled: boolean
 *
 * The emitted array is always kept in canonical LANGUAGE_OPTIONS order, so the
 * stored value is pre-sorted (display order == storage order). Theme-neutral:
 * inherits the host's text color (works on the light create modal and the dark
 * edit form alike).
 */
const CANONICAL_ORDER = LANGUAGE_OPTIONS.map((o) => o.code);

export default function LanguageMultiSelect({ value = [], onChange, disabled = false }) {
  const selected = new Set(value);

  const toggle = (code) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    // Re-emit in canonical order so the stored array stays sorted.
    onChange(CANONICAL_ORDER.filter((c) => next.has(c)));
  };

  return (
    <div className="flex flex-col gap-3">
      {LANGUAGE_GROUPS.map((group) => (
        <div key={group.title}>
          <div className="mb-1.5 text-[9px] font-medium uppercase tracking-[0.5px] opacity-50">
            {group.title}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-3">
            {group.options.map((opt) => {
              const id = `lang-${opt.code}`;
              return (
                <label
                  key={opt.code}
                  htmlFor={id}
                  className={`flex items-center gap-2 text-[13px] ${
                    disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                  }`}
                >
                  <Checkbox
                    id={id}
                    checked={selected.has(opt.code)}
                    onCheckedChange={() => toggle(opt.code)}
                    disabled={disabled}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
